// Accessibility Manager for Fortisys Website
// Enhances accessibility features and ARIA attributes

export class AccessibilityManager {
  constructor(options = {}) {
    this.options = {
      enableFocusManagement: true,
      enableLiveRegions: true,
      enableSkipLinks: true,
      announcePageChanges: true,
      ...options
    };

    this.focusHistory = [];
    this.liveRegion = null;
    this.init();
  }

  init() {
    if (this.options.enableFocusManagement) {
      this.setupFocusManagement();
    }

    if (this.options.enableLiveRegions) {
      this.setupLiveRegions();
    }

    if (this.options.enableSkipLinks) {
      this.enhanceSkipLinks();
    }

    this.enhanceExistingElements();
    this.setupKeyboardNavigation();
  }

  setupFocusManagement() {
    // Track focus for better management
    document.addEventListener('focusin', (e) => {
      this.focusHistory.push(e.target);
      // Keep only last 10 focused elements
      if (this.focusHistory.length > 10) {
        this.focusHistory.shift();
      }
    });

    // Handle focus restoration for modal dialogs
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.handleEscapeKey();
      }
    });

    // Improve focus visibility
    this.addFocusStyles();
  }

  setupLiveRegions() {
    // Create live region for announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.liveRegion.id = 'live-region';

    // Add screen reader only styles
    this.liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;

    document.body.appendChild(this.liveRegion);
  }

  enhanceSkipLinks() {
    const skipLink = document.querySelector('.skip-link');
    if (skipLink) {
      // Ensure skip link is properly styled
      skipLink.style.cssText = `
        position: absolute;
        left: -10000px;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
        z-index: 999999;
        color: #000;
        background: #fff;
        text-decoration: none;
        padding: 8px 16px;
        border-radius: 4px;
        border: 2px solid #000;
      `;

      // Show on focus
      skipLink.addEventListener('focus', () => {
        skipLink.style.cssText = `
          position: absolute;
          left: 6px;
          top: 7px;
          z-index: 999999;
          color: #000;
          background: #fff;
          text-decoration: none;
          padding: 8px 16px;
          border-radius: 4px;
          border: 2px solid #000;
          font-weight: bold;
        `;
      });

      skipLink.addEventListener('blur', () => {
        skipLink.style.left = '-10000px';
      });
    }
  }

  enhanceExistingElements() {
    this.enhanceNavigation();
    this.enhanceButtons();
    this.enhanceForms();
    this.enhanceImages();
    this.enhanceHeadings();
    this.enhanceLandmarks();
  }

  enhanceNavigation() {
    // Enhance navigation menus
    const navMenus = document.querySelectorAll('nav, .header__nav');
    navMenus.forEach(nav => {
      if (!nav.getAttribute('role')) {
        nav.setAttribute('role', 'navigation');
      }

      // Enhance dropdown menus
      const dropdowns = nav.querySelectorAll('.header__dropdown');
      dropdowns.forEach((dropdown, index) => {
        const trigger = dropdown.previousElementSibling;
        const dropdownId = `dropdown-${index}`;

        dropdown.id = dropdownId;
        dropdown.setAttribute('role', 'menu');

        if (trigger) {
          trigger.setAttribute('aria-haspopup', 'true');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', dropdownId);
        }

        // Enhance dropdown items
        const items = dropdown.querySelectorAll('a, button');
        items.forEach(item => {
          item.setAttribute('role', 'menuitem');
        });
      });
    });

    // Enhance mobile menu
    const mobileToggle = document.querySelector('.header__mobile-toggle');
    const mobileMenu = document.querySelector('.header__mobile-menu');

    if (mobileToggle && mobileMenu) {
      if (!mobileMenu.id) {
        mobileMenu.id = 'mobile-menu';
      }

      mobileToggle.setAttribute('aria-controls', mobileMenu.id);
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('role', 'navigation');
      mobileMenu.setAttribute('aria-label', 'Mobile navigation');
    }
  }

  enhanceButtons() {
    const buttons = document.querySelectorAll('button, .btn');
    buttons.forEach(button => {
      // Ensure buttons have proper roles
      if (button.tagName !== 'BUTTON' && button.tagName !== 'A') {
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
      }

      // Add descriptive labels for icon buttons
      if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
        const icon = button.querySelector('.icon, [class*="icon-"]');
        if (icon) {
          button.setAttribute('aria-label', 'Button');
        }
      }

      // Enhance loading states
      button.addEventListener('click', () => {
        if (button.classList.contains('btn--loading')) {
          button.setAttribute('aria-busy', 'true');
        }
      });
    });
  }

  enhanceForms() {
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      // Ensure form has proper attributes
      if (!form.getAttribute('novalidate')) {
        form.setAttribute('novalidate', '');
      }

      // Enhance form fields
      const fields = form.querySelectorAll('input, textarea, select');
      fields.forEach(field => {
        const label = form.querySelector(`label[for="${field.id}"]`);
        const fieldName = field.name || field.id;

        // Create error container if it doesn't exist
        let errorContainer = form.querySelector(`#${fieldName}-error`);
        if (!errorContainer) {
          errorContainer = document.createElement('div');
          errorContainer.id = `${fieldName}-error`;
          errorContainer.className = 'form-error';
          errorContainer.setAttribute('role', 'alert');
          errorContainer.setAttribute('aria-live', 'polite');
          errorContainer.style.display = 'none';

          // Insert after field or its parent form group
          const formGroup = field.closest('.form-group') || field.parentNode;
          formGroup.appendChild(errorContainer);
        }

        // Link field to error container
        field.setAttribute('aria-describedby', errorContainer.id);

        // Enhance required fields
        if (field.hasAttribute('required')) {
          field.setAttribute('aria-required', 'true');

          // Mark label as required if not already done
          if (label && !label.querySelector('.form-required')) {
            const requiredMarker = document.createElement('span');
            requiredMarker.className = 'form-required';
            requiredMarker.textContent = ' *';
            requiredMarker.setAttribute('aria-label', 'required');
            label.appendChild(requiredMarker);
          }
        }
      });
    });
  }

  enhanceImages() {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      // Ensure decorative images have empty alt
      if (!img.hasAttribute('alt')) {
        // Check if image is decorative (in certain contexts)
        const isDecorative = img.closest('.hero__visual, .trust-indicators, [aria-hidden="true"]');
        img.setAttribute('alt', isDecorative ? '' : 'Image');
      }

      // Handle loading states
      if (img.loading === 'lazy') {
        img.addEventListener('load', () => {
          img.removeAttribute('aria-busy');
        });

        img.addEventListener('error', () => {
          img.setAttribute('aria-label', 'Image failed to load');
        });
      }
    });
  }

  enhanceHeadings() {
    // Check heading hierarchy
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let previousLevel = 0;

    headings.forEach(heading => {
      const currentLevel = parseInt(heading.tagName.charAt(1));

      // Warn about skipped heading levels (for development)
      if (currentLevel > previousLevel + 1) {
        console.warn(`Heading level ${currentLevel} follows level ${previousLevel}. Consider using h${previousLevel + 1} instead.`);
      }

      // Add IDs for anchor links if missing
      if (!heading.id && heading.textContent.trim()) {
        heading.id = this.createSlug(heading.textContent);
      }

      previousLevel = currentLevel;
    });
  }

  enhanceLandmarks() {
    // Ensure main landmarks have proper labels
    const landmarks = document.querySelectorAll('main, nav, aside, section[role="banner"], section[role="contentinfo"]');
    landmarks.forEach(landmark => {
      const role = landmark.getAttribute('role') || landmark.tagName.toLowerCase();

      if (role === 'navigation' && !landmark.getAttribute('aria-label')) {
        const isMain = landmark.closest('header');
        landmark.setAttribute('aria-label', isMain ? 'Main navigation' : 'Navigation');
      }
    });
  }

  setupKeyboardNavigation() {
    // Enhance keyboard navigation for custom interactive elements
    document.addEventListener('keydown', (e) => {
      const activeElement = document.activeElement;

      // Handle Enter and Space for custom buttons
      if ((e.key === 'Enter' || e.key === ' ') && activeElement.getAttribute('role') === 'button') {
        e.preventDefault();
        activeElement.click();
      }

      // Handle arrow keys in menus
      if (activeElement.closest('[role="menu"]')) {
        this.handleMenuNavigation(e);
      }
    });
  }

  handleMenuNavigation(event) {
    const menu = event.target.closest('[role="menu"]');
    const menuItems = menu.querySelectorAll('[role="menuitem"]');
    const currentIndex = Array.from(menuItems).indexOf(event.target);

    let nextIndex;

    switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      nextIndex = currentIndex + 1 < menuItems.length ? currentIndex + 1 : 0;
      menuItems[nextIndex].focus();
      break;

    case 'ArrowUp':
      event.preventDefault();
      nextIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : menuItems.length - 1;
      menuItems[nextIndex].focus();
      break;

    case 'Home':
      event.preventDefault();
      menuItems[0].focus();
      break;

    case 'End':
      event.preventDefault();
      menuItems[menuItems.length - 1].focus();
      break;

    case 'Escape':
      event.preventDefault();
      this.closeMenu(menu);
      break;
    }
  }

  closeMenu(menu) {
    // Find and focus the trigger
    const trigger = document.querySelector(`[aria-controls="${menu.id}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }

    // Hide menu
    menu.style.display = 'none';
  }

  handleEscapeKey() {
    // Close any open dropdowns or modals
    const openDropdowns = document.querySelectorAll('[aria-expanded="true"]');
    openDropdowns.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      const controlledElement = document.getElementById(trigger.getAttribute('aria-controls'));
      if (controlledElement) {
        controlledElement.style.display = 'none';
      }
    });
  }

  addFocusStyles() {
    // Add global focus styles if not present
    if (!document.querySelector('#accessibility-focus-styles')) {
      const style = document.createElement('style');
      style.id = 'accessibility-focus-styles';
      style.textContent = `
        /* High contrast focus indicators */
        *:focus {
          outline: 2px solid #005fcc;
          outline-offset: 2px;
        }

        /* Enhanced focus for interactive elements */
        button:focus,
        a:focus,
        input:focus,
        textarea:focus,
        select:focus,
        [tabindex]:focus {
          outline: 2px solid #005fcc;
          outline-offset: 2px;
          box-shadow: 0 0 0 4px rgba(0, 95, 204, 0.25);
        }

        /* Screen reader only class */
        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }

        /* Skip link enhancement */
        .skip-link:focus {
          position: absolute;
          left: 6px;
          top: 7px;
          z-index: 999999;
          color: #000;
          background: #fff;
          text-decoration: none;
          padding: 8px 16px;
          border-radius: 4px;
          border: 2px solid #000;
          font-weight: bold;
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Public API methods
  announce(message, priority = 'polite') {
    if (!this.liveRegion) return;

    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      this.liveRegion.textContent = '';
    }, 1000);
  }

  createSlug(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  restoreFocus() {
    if (this.focusHistory.length > 1) {
      const previousElement = this.focusHistory[this.focusHistory.length - 2];
      if (previousElement && document.contains(previousElement)) {
        previousElement.focus();
      }
    }
  }

  destroy() {
    // Remove live region
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.remove();
    }

    // Remove added styles
    const focusStyles = document.querySelector('#accessibility-focus-styles');
    if (focusStyles) {
      focusStyles.remove();
    }

    // Clear focus history
    this.focusHistory = [];
  }
}
